import React, {useState} from 'react';
import {Modal, Button, Form} from 'react-bootstrap';
import styles from './EditSubscriptionModal.module.scss';

interface EditSubscriptionModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (data: EditSubscriptionData) => void;
  subscription: {
    id: number;
    name: string;
    reference: string;
    startDate: string;
    endDate: string;
    autoRenewal: boolean;
    status: string;
    siteName: string;
    siteAddress: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    billingPeriod: string;
    productName: string;
    productDescription: string;
  };
}

export interface EditSubscriptionData {
  reference: string;
  startDate: string;
  endDate: string;
  autoRenewal: boolean;
  status: string;
  siteName: string;
  siteAddress: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  billingPeriod: string;
  productName: string;
  productDescription: string;
}


export interface EditSubscriptionData {
  reference: string;
  startDate: string;
  endDate: string;
  autoRenewal: boolean;
  siteName: string;
  siteAddress: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  billingPeriod: string;
  productName: string;
  productDescription: string;
}

const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
  show,
  onHide,
  onSave,
  subscription,
}) => {
  const [formData, setFormData] = useState<EditSubscriptionData>({
    reference: subscription.reference,
    startDate: subscription.startDate,
    endDate: subscription.endDate,
    autoRenewal: subscription.autoRenewal,
    status: subscription.status,
    siteName: subscription.siteName,
    siteAddress: subscription.siteAddress,
    contactName: subscription.contactName,
    contactEmail: subscription.contactEmail,
    contactPhone: subscription.contactPhone,
    billingPeriod: subscription.billingPeriod,
    productName: subscription.productName,
    productDescription: subscription.productDescription,
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleChange = (field: keyof EditSubscriptionData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.reference.trim()) {
      newErrors.reference = 'La référence client est obligatoire';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est obligatoire';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La date de fin est obligatoire';
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate.split('/').reverse().join('-'));
      const end = new Date(formData.endDate.split('/').reverse().join('-'));
      if (end <= start) {
        newErrors.endDate = 'La date de fin doit être après la date de début';
      }
    }

    if (!formData.status.trim()) {
      newErrors.status = 'Le statut est obligatoire';
    }

    if (!formData.productName.trim()) {
      newErrors.productName = 'Le nom du service est obligatoire';
    }

    if (!formData.siteName.trim()) {
      newErrors.siteName = 'Le nom du site est obligatoire';
    }

    if (!formData.siteAddress.trim()) {
      newErrors.siteAddress = "L'adresse du site est obligatoire";
    }

    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Le nom du contact est obligatoire';
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = "L'email du contact est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "L'email n'est pas valide";
    }

    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Le téléphone du contact est obligatoire';
    }

    if (!formData.billingPeriod.trim()) {
      newErrors.billingPeriod = 'La période de facturation est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onHide();
    }
  };

  const handleCancel = () => {
    setFormData({
      reference: subscription.reference,
      startDate: subscription.startDate,
      endDate: subscription.endDate,
      autoRenewal: subscription.autoRenewal,
      status: subscription.status,
      siteName: subscription.siteName,
      siteAddress: subscription.siteAddress,
      contactName: subscription.contactName,
      contactEmail: subscription.contactEmail,
      contactPhone: subscription.contactPhone,
      billingPeriod: subscription.billingPeriod,
      productName: subscription.productName,
      productDescription: subscription.productDescription,
    });
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Modifier l'abonnement</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-4">
            {/* Champs non modifiables */}
            <div className={styles.readOnlySection}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">ID abonnement</Form.Label>
                <Form.Control
                  type="text"
                  value={subscription.id}
                  disabled
                  className={styles.readOnlyField}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Offre</Form.Label>
                <Form.Control
                  type="text"
                  value={subscription.name}
                  disabled
                  className={styles.readOnlyField}
                />
              </Form.Group>
            </div>

            <div className={styles.separator}></div>

            {/* Section Informations générales */}
            <h6 className="fw-bold mb-3 mt-4">Informations générales</h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Référence Client <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.reference}
                onChange={(e) => handleChange('reference', e.target.value)}
                isInvalid={!!errors.reference}
                placeholder="Saisissez la référence client"
              />
              <Form.Control.Feedback type="invalid">
                {errors.reference}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Date de début <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={formData.startDate.split('/').reverse().join('-')}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const formatted = date.toLocaleDateString('fr-FR');
                  handleChange('startDate', formatted);
                }}
                isInvalid={!!errors.startDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.startDate}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Date de fin <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={formData.endDate.split('/').reverse().join('-')}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const formatted = date.toLocaleDateString('fr-FR');
                  handleChange('endDate', formatted);
                }}
                isInvalid={!!errors.endDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.endDate}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Statut <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                isInvalid={!!errors.status}
              >
                <option value="">Sélectionnez un statut</option>
                <option value="Actif">Actif</option>
                <option value="En attente">En attente</option>
                <option value="Suspendu">Suspendu</option>
                <option value="Expiré">Expiré</option>
                <option value="Annulé">Annulé</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.status}
              </Form.Control.Feedback>
            </Form.Group>

            <div className={styles.separator}></div>

            {/* Section Détails de l'offre */}
            <h6 className="fw-bold mb-3 mt-4">Détails de l'offre</h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Offre/Service <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.productName}
                onChange={(e) => {
                  const selectedOption = e.target.options[e.target.selectedIndex];
                  const description = selectedOption.getAttribute('data-description') || '';
                  handleChange('productName', e.target.value);
                  handleChange('productDescription', description);
                }}
                isInvalid={!!errors.productName}
              >
                <option value="">Sélectionnez un service</option>
                <option value="Surveillance 24/7" data-description="Service de surveillance et monitoring continu de votre infrastructure">
                  Surveillance 24/7
                </option>
                <option value="Maintenance Préventive" data-description="Maintenance préventive mensuelle incluant mises à jour et optimisations">
                  Maintenance Préventive
                </option>
                <option value="Support Technique Premium" data-description="Support technique prioritaire avec temps de réponse garanti sous 2h">
                  Support Technique Premium
                </option>
                <option value="Hébergement Cloud" data-description="Solution d'hébergement cloud sécurisé avec backup quotidien">
                  Hébergement Cloud
                </option>
                <option value="Sécurité Avancée" data-description="Protection avancée contre les menaces avec pare-feu et antivirus managé">
                  Sécurité Avancée
                </option>
                <option value="Sauvegarde et Restauration" data-description="Service de sauvegarde automatique et restauration de données">
                  Sauvegarde et Restauration
                </option>
                <option value="Infogérance Complète" data-description="Gestion complète de votre infrastructure IT avec SLA garantis">
                  Infogérance Complète
                </option>
                <option value="Consulting IT" data-description="Accompagnement et conseil stratégique pour votre transformation digitale">
                  Consulting IT
                </option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.productName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.productDescription}
                onChange={(e) => handleChange('productDescription', e.target.value)}
                placeholder="Description du service"
                readOnly
                className={styles.readOnlyField}
              />
              <Form.Text className="text-muted">
                La description est automatiquement remplie selon le service sélectionné
              </Form.Text>
            </Form.Group>

            <div className={styles.separator}></div>

            {/* Section Facturation */}
            <h6 className="fw-bold mb-3 mt-4">Facturation</h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Période de facturation <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.billingPeriod}
                onChange={(e) => handleChange('billingPeriod', e.target.value)}
                isInvalid={!!errors.billingPeriod}
              >
                <option value="">Sélectionnez une période</option>
                <option value="Mensuel">Mensuel</option>
                <option value="Trimestriel">Trimestriel</option>
                <option value="Semestriel">Semestriel</option>
                <option value="Annuel">Annuel</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.billingPeriod}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="autoRenewal"
                label="Renouvellement automatique"
                checked={formData.autoRenewal}
                onChange={(e) => handleChange('autoRenewal', e.target.checked)}
              />
            </Form.Group>

            <div className={styles.separator}></div>

            {/* Section Informations du site */}
            <h6 className="fw-bold mb-3 mt-4">Informations du site</h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Nom du site <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.siteName}
                onChange={(e) => handleChange('siteName', e.target.value)}
                isInvalid={!!errors.siteName}
                placeholder="Nom du site"
              />
              <Form.Control.Feedback type="invalid">
                {errors.siteName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Adresse <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.siteAddress}
                onChange={(e) => handleChange('siteAddress', e.target.value)}
                isInvalid={!!errors.siteAddress}
                placeholder="Adresse complète du site"
              />
              <Form.Control.Feedback type="invalid">
                {errors.siteAddress}
              </Form.Control.Feedback>
            </Form.Group>

            <div className={styles.separator}></div>

            {/* Section Contact */}
            <h6 className="fw-bold mb-3 mt-4">Contact</h6>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Nom <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.contactName}
                onChange={(e) => handleChange('contactName', e.target.value)}
                isInvalid={!!errors.contactName}
                placeholder="Nom du contact"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                isInvalid={!!errors.contactEmail}
                placeholder="email@example.com"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactEmail}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                Téléphone <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
                isInvalid={!!errors.contactPhone}
                placeholder="+33 1 23 45 67 89"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactPhone}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            Annuler
          </Button>
          <Button variant="primary" type="submit">
            <i className="fa fa-save me-2"></i>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditSubscriptionModal;
